import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Circle } from '../objects/Circle';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { DrawPoint, DrawLine, DrawCircle, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

export class RegularPolygonConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; radius?: number } = {}): ConstructionStep[] {
    const center = { x: options.x ?? 400, y: options.y ?? 350 };
    const r = options.radius ?? 180;

    // Calculate 6 vertices of regular hexagon
    const vertices = Array.from({ length: 6 }, (_, i) => {
      const angle = (i * Math.PI) / 3;
      return SVGRenderer.polarToCartesian(center, r, angle);
    });

    const pointO = new Point(this.renderer, center.x, center.y, 'O');
    const mainCircle = new Circle(this.renderer, center, r, false);

    const vertexPoints = vertices.map((v, idx) => new Point(this.renderer, v.x, v.y, `V${idx + 1}`));
    // stepArcs: CW -36°→36° = 72° small arc centered on 0° ✓
    const stepArcs = vertices.map((v) => new Arc(this.renderer, v, r, -Math.PI * 0.2, Math.PI * 0.2, true, true));

    const edges = vertices.map((v, i) => {
      const nextV = vertices[(i + 1) % 6];
      return new Segment(this.renderer, v, nextV, false);
    });

    [pointO, mainCircle, ...vertexPoints, ...stepArcs, ...edges].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Center and Main Circle',
        description: 'Place compass at center O and draw circle of radius R.',
        narration: 'Step 1: Drawing main bounding circle centered at O.',
        duration: 1800,
        animations: [new DrawPoint(pointO), new MoveCompass(this.compass, center, r), new DrawCircle(mainCircle, this.compass)],
      },
      {
        id: 'step2',
        title: '2. Step Compass Radius along Perimeter',
        description: 'Starting at V1, step compass around circle to mark all 6 vertices.',
        narration: 'Step 2: Stepping compass radius around circle perimeter.',
        duration: 2500,
        animations: [
          new MoveCompass(this.compass, vertices[0], r),
          new DrawPoint(vertexPoints[0]),
          new DrawArc(stepArcs[0], this.compass),
          new DrawPoint(vertexPoints[1]),
          new MoveCompass(this.compass, vertices[1], r),
          new DrawArc(stepArcs[1], this.compass),
          new DrawPoint(vertexPoints[2]),
        ],
      },
      {
        id: 'step3',
        title: '3. Complete 6 Perimeter Intersections',
        description: 'Mark remaining vertices V4, V5, V6.',
        narration: 'Step 3: Marking all 6 equal vertex positions on the circle.',
        duration: 2000,
        animations: [
          new DrawPoint(vertexPoints[3]),
          new DrawPoint(vertexPoints[4]),
          new DrawPoint(vertexPoints[5]),
        ],
      },
      {
        id: 'step4',
        title: '4. Connect Hexagon Edges with Ruler',
        description: 'Use ruler to connect adjacent vertices to form regular hexagon.',
        narration: 'Step 4: Connecting all vertices with the ruler.',
        duration: 2500,
        animations: [
          new Fade(this.compass, false),
          ...edges.map((edge) => new DrawLine(edge, this.ruler)),
        ],
      },
      {
        id: 'step5',
        title: '5. Highlight Regular Hexagon',
        description: 'Highlight completed regular 6-sided polygon.',
        narration: 'Construction complete! Inscribed regular hexagon finished.',
        duration: 1500,
        animations: [
          new Fade(this.ruler, false),
          ...edges.map((edge) => new Highlight(edge)),
        ],
      },
    ];
  }
}
